/**
 * InstancePicker
 *
 * Persistent source → target picker for the central-MU model. Used by every migration flow
 * (process, ontology) as the first decision. Backed by useInstancePairStore so the choice
 * survives navigation between pages within a session.
 *
 * Visual: two Selects with an ArrowLeftRight swap button between them. Each option shows the
 * instance name; status badge displayed on the right of the dropdown when an instance is picked.
 */

import React from "react";
import { ArrowLeftRight, ArrowRight, Server } from "lucide-react";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { cn } from "../lib/utils";
import { useInstances, type Instance } from "../lib/api-central-mu";
import { useInstancePairStore } from "../store/useInstancePairStore";

interface InstancePickerProps {
  /** Compact: hides the wrapper Card; for embedding inside another card. */
  compact?: boolean;
  /**
   * Locks both selects + swap. Set once a migration flow has advanced past its first step so the
   * pair can't change mid-flow (stale facility/checklist selections against a different instance
   * made the migration a silent no-op that still reported success).
   */
  disabled?: boolean;
  className?: string;
}

export function InstancePicker({ compact = false, disabled = false, className }: InstancePickerProps) {
  const { data: instances = [], isLoading, error } = useInstances();
  const sourceServiceId = useInstancePairStore((s) => s.sourceServiceId);
  const targetServiceId = useInstancePairStore((s) => s.targetServiceId);
  const setSource = useInstancePairStore((s) => s.setSource);
  const setTarget = useInstancePairStore((s) => s.setTarget);
  const swap = useInstancePairStore((s) => s.swap);

  const source = instances.find((i) => i.id === sourceServiceId) ?? null;
  const target = instances.find((i) => i.id === targetServiceId) ?? null;

  const body = (
    <div className={cn("space-y-4", className)}>
      {error ? (
        <div className="text-sm text-destructive">Failed to load instances</div>
      ) : isLoading ? (
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : instances.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          No instances are registered. Ask an admin to add a row in <code>connected_services</code>.
        </div>
      ) : instances.length === 1 ? (
        <div className="text-sm text-muted-foreground">
          You only have access to one instance ({instances[0].name}). Ask an admin to grant access to another instance
          before migrating.
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
          {/* Source */}
          <div className="space-y-2">
            <Label htmlFor="instance-source">Source instance</Label>
            <Select
              value={sourceServiceId != null ? String(sourceServiceId) : undefined}
              onValueChange={(v) => setSource(Number(v))}
              disabled={disabled}
            >
              <SelectTrigger id="instance-source" className="w-full">
                <SelectValue placeholder="Pick a source…">
                  {source ? <InstanceOption inst={source} compact /> : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {instances.map((inst) => (
                  <SelectItem
                    key={inst.id}
                    value={String(inst.id)}
                    disabled={inst.id === targetServiceId}
                  >
                    <InstanceOption inst={inst} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Swap button */}
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="mb-px"
            aria-label="Swap source and target"
            onClick={swap}
            disabled={disabled || (sourceServiceId == null && targetServiceId == null)}
          >
            <ArrowLeftRight className="size-4" />
          </Button>

          {/* Target */}
          <div className="space-y-2">
            <Label htmlFor="instance-target">Target instance</Label>
            <Select
              value={targetServiceId != null ? String(targetServiceId) : undefined}
              onValueChange={(v) => setTarget(Number(v))}
              disabled={disabled}
            >
              <SelectTrigger id="instance-target" className="w-full">
                <SelectValue placeholder="Pick a target…">
                  {target ? <InstanceOption inst={target} compact /> : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {instances.map((inst) => (
                  <SelectItem
                    key={inst.id}
                    value={String(inst.id)}
                    disabled={inst.id === sourceServiceId}
                  >
                    <InstanceOption inst={inst} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Summary line when both are picked */}
      {source && target ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Server className="size-4" />
          <span>
            Migrating from <span className="font-medium text-foreground">{source.name}</span>
          </span>
          <ArrowRight className="size-4" />
          <span className="font-medium text-foreground">{target.name}</span>
        </div>
      ) : null}

      {disabled ? (
        <p className="text-xs text-muted-foreground">
          Instance selection is locked. Go back to the Select Instances page to change it.
        </p>
      ) : null}
    </div>
  );

  if (compact) return body;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Choose instances</CardTitle>
        <CardDescription>
          Pick which Leucine instance the data comes <em>from</em> (source) and which one it goes <em>to</em> (target).
        </CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

function InstanceOption({ inst, compact = false }: { inst: Instance; compact?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className={cn("size-2 rounded-full", inst.active ? "bg-emerald-500" : "bg-zinc-400")} />
      <span>{inst.name}</span>
      {!compact && inst.baseUrl ? (
        <Badge variant="outline" className="ml-auto text-xs font-normal text-muted-foreground">
          {prettyUrl(inst.baseUrl)}
        </Badge>
      ) : null}
    </div>
  );
}

function prettyUrl(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** Compact read-only badge used in the workflow list to show source → target. */
export function InstancePairBadge({
  sourceName,
  targetName,
}: {
  sourceName: string | null | undefined;
  targetName: string | null | undefined;
}) {
  if (!sourceName && !targetName) {
    return <span className="text-xs text-muted-foreground">(legacy)</span>;
  }
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant="outline" className="font-normal">
        {sourceName ?? "?"}
      </Badge>
      <ArrowRight className="size-3 text-muted-foreground" />
      <Badge variant="outline" className="font-normal">
        {targetName ?? "?"}
      </Badge>
    </div>
  );
}
